import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ViewModuleOutlinedIcon from '@mui/icons-material/ViewModuleOutlined';
import ChecklistIcon from '@mui/icons-material/Checklist';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { cohorts, formatDate } from '../../data/liveClasses';
import './index.css';

const ANIMATION_MS = 280;

const nextCohortFor = (course) =>
  cohorts
    .filter((c) => c.course === course)
    .sort((a, b) => a.startDate.localeCompare(b.startDate))[0];

// Centred modal on desktop, bottom sheet on phones. Shows a course summary and its module roadmap.
const CourseModal = ({ course, onClose }) => {
  // Keep the last course rendered while the close animation plays.
  const [shown, setShown] = useState(course);
  const [closing, setClosing] = useState(false);
  const closeRef = useRef(null);

  useEffect(() => {
    if (course) {
      setShown(course);
      setClosing(false);
    } else if (shown) {
      setClosing(true);
      const timer = setTimeout(() => {
        setShown(null);
        setClosing(false);
      }, ANIMATION_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course]);

  useEffect(() => {
    if (!course) return undefined;
    const onKey = (e) => e.key === 'Escape' && onClose();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [course, onClose]);

  if (!shown) return null;

  const { title, description, level, duration, icon: Icon, accent, stack, tags, isNew, curriculumData } = shown;
  const modules = curriculumData.curriculum;
  const topicCount = modules.reduce((sum, m) => sum + m.topics.length, 0);
  const cohort = nextCohortFor(shown);

  return (
    <div
      className={`course-modal-root ${closing ? 'is-closing' : ''}`}
      style={{ '--c1': accent[0], '--c2': accent[1], '--anim': `${ANIMATION_MS}ms` }}
    >
      <div className="course-modal-backdrop" onClick={onClose} />
      <div className="course-modal" role="dialog" aria-modal="true" aria-labelledby="course-modal-title">
        <button type="button" className="course-modal-close" onClick={onClose} ref={closeRef} aria-label="Close">
          <CloseIcon />
        </button>

        <aside className="course-modal-summary">
          <div className="course-modal-glow" aria-hidden="true" />
          <div className="course-modal-badges">
            <span className="course-modal-icon"><Icon /></span>
            {isNew && <span className="course-modal-badge course-modal-badge--new">New</span>}
            <span className="course-modal-badge">{level}</span>
          </div>

          <h2 id="course-modal-title">{curriculumData.title || title}</h2>
          <p className="course-modal-desc">{description}</p>

          {stack ? (
            <div className="course-modal-stack">
              {stack.map((s) => (
                <span key={s.name} title={s.name}><img src={s.logo} alt={s.name} /></span>
              ))}
            </div>
          ) : (
            <div className="course-modal-tags">
              {tags?.map((t) => <span key={t}>{t}</span>)}
            </div>
          )}

          <dl className="course-modal-stats">
            <div><ScheduleIcon /><dt>Duration</dt><dd>{duration}</dd></div>
            <div><ViewModuleOutlinedIcon /><dt>Modules</dt><dd>{modules.length}</dd></div>
            <div><ChecklistIcon /><dt>Topics</dt><dd>{topicCount}</dd></div>
          </dl>

          {cohort && (
            <div className="course-modal-cohort">
              <span className="course-modal-live" />
              <CalendarMonthIcon sx={{ fontSize: 18 }} />
              <div>
                <strong>Next live cohort · {formatDate(cohort.startDate)}</strong>
                <span>{cohort.schedule}</span>
              </div>
            </div>
          )}

          <div className="course-modal-ctas">
            <Link to={`/live-classes?course=${encodeURIComponent(title)}#request-form`} className="course-modal-primary" onClick={onClose}>
              Request a seat <ArrowForwardIcon sx={{ fontSize: 18 }} />
            </Link>
            <Link to="/learn" className="course-modal-secondary" onClick={onClose}>Try free lessons</Link>
          </div>
        </aside>

        <section className="course-modal-roadmap" aria-label="Curriculum">
          <div className="course-modal-roadmap-head">
            <h3>Curriculum roadmap</h3>
            <span>{modules.length} modules · {topicCount} topics</span>
          </div>
          <ol className="roadmap">
            {modules.map((m, i) => (
              <li className="roadmap-item" key={m.section} style={{ '--i': i }}>
                <span className="roadmap-node">{String(i + 1).padStart(2, '0')}</span>
                <div className="roadmap-card">
                  <div className="roadmap-card-head">
                    <h4>{m.section}</h4>
                    <span>{m.topics.length} topics</span>
                  </div>
                  <ul>
                    {m.topics.map((t) => <li key={t}>{t}</li>)}
                  </ul>
                </div>
              </li>
            ))}
          </ol>
        </section>
      </div>
    </div>
  );
};

export default CourseModal;
